import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { IFeature } from 'graphql/types';
import { PROFILE_SECTIONS, USER_SECTIONS } from 'user/constants/sections';
import { TTabsMenuItem } from 'common/types';
import { NotFound } from 'common/components/NotFound';
import { useFeature } from 'common/hooks/useFeature';
import { IUserProfileSectionProps } from 'user/types';

import { useUserRoutes } from '.';

const BUDGET_SECTIONS = ['PORTFOLIO_BUDGET', 'USER_BUDGET'];

interface IState {
    tabs: TTabsMenuItem[];
    Section?: React.FC<React.PropsWithChildren<IUserProfileSectionProps>>;
    canUsePortfolioOffers?: boolean;
    hideOffersSection?: boolean;
    hideMeetingsSection?: boolean;
}

export const useUserProfileTabs = (
    id: string,
    isCurrentUser?: boolean,
    canUsePortfolioOffers?: boolean,
    hideOffersSection?: boolean,
    hideMeetingsSection?: boolean,
    canUseSkills?: boolean
): IState => {
    const { activeSectionRoute } = useUserRoutes();
    const [translate] = useTranslation();

    // The budget tab only exists for organisations with the budget feature
    const { canUse: canUseBudget, loading: loadingCanUseBudget } = useFeature(
        IFeature.Budget
    );

    const getState = useCallback(() => {
        const tabs: TTabsMenuItem[] = Object.keys(
            isCurrentUser ? PROFILE_SECTIONS : USER_SECTIONS
        )
            .map((section) => {
                const isOffersSection =
                    section === 'PORTFOLIO_OFFERS' || section === 'USER_OFFERS';
                const isMeetingsSection =
                    section === 'PORTFOLIO_MEETINGS' ||
                    section === 'USER_MEETINGS';
                const offerOrMeetingSection =
                    isOffersSection || isMeetingsSection;
                const hideAllOfferTabs =
                    offerOrMeetingSection && !canUsePortfolioOffers;

                const isSkillsSection =
                    section === 'PORTFOLIO_SKILLS' ||
                    section === 'USER_PORTFOLIO_SKILLS';

                const isBudgetSection = BUDGET_SECTIONS.includes(section);

                if (
                    hideAllOfferTabs ||
                    (hideOffersSection && isOffersSection) ||
                    (hideMeetingsSection && isMeetingsSection) ||
                    (isSkillsSection && !canUseSkills) ||
                    (isBudgetSection && !canUseBudget)
                ) {
                    return null;
                }

                return {
                    label: translate(`userProfileTabs.${section}`),
                    to: section,
                    params: { id },
                    value: section,
                };
            })
            .filter(Boolean);

        const hasCurrentTab = tabs.find(
            (item) => item.value === activeSectionRoute
        );

        let Section = isCurrentUser
            ? PROFILE_SECTIONS.PORTFOLIO_TRAININGS
            : USER_SECTIONS.USER_TRAININGS;

        if (hasCurrentTab) {
            Section = (isCurrentUser ? PROFILE_SECTIONS : USER_SECTIONS)[
                activeSectionRoute
            ];
        }

        // The budget routes exist but the organisation cannot use them, so
        // they are a dead end instead of falling back to another section.
        if (BUDGET_SECTIONS.includes(activeSectionRoute) && !canUseBudget) {
            // Render nothing until the feature is known, to prevent showing
            // the not found page to organisations that do have the feature.
            return {
                tabs,
                Section: loadingCanUseBudget ? undefined : NotFound,
            };
        }

        return { tabs, Section };
    }, [
        activeSectionRoute,
        translate,
        id,
        isCurrentUser,
        canUsePortfolioOffers,
        canUseSkills,
        canUseBudget,
        loadingCanUseBudget,
    ]);

    const [state, setState] = useState<IState>(getState());

    useEffect(() => {
        setState(getState());
    }, [activeSectionRoute, translate, id, isCurrentUser, getState]);

    return state;
};
